import {forwardRef, useMemo} from 'react';
import {Box, MenuItem, Typography} from '@mui/material';

import {ObjectSelect, ObjectSelectProps} from '@shared/components';
import {useGetCategories} from '../../../../entities/category/apiHooks';
import {Category} from '../../../../entities/category/types';

type Props = Omit<
  ObjectSelectProps<Category>,
  'options' | 'labelProp' | 'valueProp' | 'value' | 'onChange'
> & {
  value: string | null;
  onChange: (categoryId: string | null) => void;
  placeholder?: string;
};

const ALL_CATEGORY: Category = {
  id: '',
  name: 'Category',
  description: '',
  logoUrl: '',
  bannerUrl: '',
  order: 0,
};

export const CategoryFilter = forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const {
      value: categoryId,
      onChange,
      placeholder = 'Category',
      disabled,
      ...restProps
    } = props;

    const {data, isPending} = useGetCategories();
    const categories = data ?? [];

    const options = useMemo(() => [ALL_CATEGORY, ...categories], [categories]);

    const selectedCategory = useMemo<Category>(() => {
      return options.find(c => c.id === (categoryId ?? '')) ?? ALL_CATEGORY;
    }, [options, categoryId]);

    const handleChange: ObjectSelectProps<Category>['onChange'] = next => {
      if (!next) {
        onChange(null);
        return;
      }

      if (Array.isArray(next)) {
        const first = next[0];
        onChange(first && first.id !== '' ? first.id : null);
        return;
      }

      onChange(next.id === '' ? null : next.id);
    };

    return (
      <ObjectSelect<Category>
        {...restProps}
        ref={ref}
        disabled={isPending || Boolean(disabled)}
        options={options}
        labelProp="name"
        valueProp="id"
        value={selectedCategory}
        onChange={handleChange}
        displayEmpty
        renderValue={value => {
          const id = Array.isArray(value) ? value[0] : value;

          if (!id) {
            return <span style={{color: '#9e9e9e'}}>{placeholder}</span>;
          }

          const category = options.find(c => c.id === id);

          if (!category || category.id === '') {
            return <span style={{color: '#9e9e9e'}}>{placeholder}</span>;
          }

          return (
            <Box display="flex" alignItems="center" gap={1.5}>
              {category.logoUrl && (
                <img src={category.logoUrl} alt="" height={24} />
              )}
              <Typography>{category.name}</Typography>
            </Box>
          );
        }}
        renderOption={category => {
          if (category.id === '') {
            return (
              <MenuItem key="__all" value="">
                <Typography sx={{color: '#9e9e9e'}}>{placeholder}</Typography>
              </MenuItem>
            );
          }

          return (
            <MenuItem key={category.id} value={category.id}>
              <Box display="flex" alignItems="center" gap={1.5}>
                {category.logoUrl && (
                  <img src={category.logoUrl} alt="" height={24} />
                )}
                <Typography>{category.name}</Typography>
              </Box>
            </MenuItem>
          );
        }}
      />
    );
  },
);
